import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Tab } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { ExternalReferralSearch } from '../../components/ExternalReferral/ExternalReferralSearch';
import { TabsHistory } from '../../../app/components/TabsHistory';
import FormRow from '../../../app/components/FormRow';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useNavigation } from '../../../common/navigation';
import { useCookies } from '../../../common/hooks/useCookies';
import CompanyByNameOrNipSelector from '../../components/Company/CompanyByNameOrNipSelector';

export const ExternalReferralsForRegistrationView: React.FC = () => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const cookies = useCookies();
  const [companyId, setCompanyId] = useState<string | undefined>(
    cookies.selectedCompany || undefined,
  );

  useEffect(() => {
    if (cookies.activeMonitor !== '') {
      cookies.setActiveMonitor('');
    }
  }, [cookies]);

  const handleSelect = (companyId: string | undefined) => {
    setCompanyId(companyId || undefined);
    cookies.setSelectedCompany(companyId);
  };

  const handleClick = async (id: string) => {
    void nav.examTests(id);
  };

  const getDataWithoutJudgments = useMemo(
    () =>
      ((pageSize, pageNumber, orderBy, orderDirection, filterText) =>
        spd.externalReferrals.getWithoutJudgmentsByPortion(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          companyId,
        )) as GridGetterFunction,
    [spd.externalReferrals, companyId],
  );

  const getCountWithoutJudgments = useMemo(
    () =>
      (filterText =>
        spd.externalReferrals.getWithoutJudgmentsCount(
          filterText,
          companyId,
        )) as GridCountFunction,
    [spd.externalReferrals, companyId],
  );

  const getAllData = useMemo(
    () =>
      ((pageSize, pageNumber, orderBy, orderDirection, filterText) =>
        spd.externalReferrals.getAllByPortion(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          companyId,
        )) as GridGetterFunction,
    [spd.externalReferrals, companyId],
  );

  const getAllCount = useMemo(
    () =>
      (filterText =>
        spd.externalReferrals.getAllCount(
          filterText,
          companyId,
        )) as GridCountFunction,
    [spd.externalReferrals, companyId],
  );

  return (
    <Card>
      <Card.Body className="mb-4">
        <Card.Title className="pb-3">Skierowania zewnętrzne</Card.Title>
        <Form className="d-grid gap-4 mb-5">
          <FormRow controlId="company" label="Firma/filia">
            <CompanyByNameOrNipSelector
              value={companyId || null}
              onChange={e => handleSelect(e.value)}
              clearable={true}
            />
          </FormRow>
        </Form>
        <TabsHistory
          activeKey={'activeExternalReferralsWithoutJudgments'}
          id="externalReferrals"
          className="mb-3"
          mountOnEnter={true}
          unmountOnExit={true}
        >
          <Tab
            eventKey="activeExternalReferralsWithoutJudgments"
            title="Zakończone z orzeczeniami do wydania"
            className="m-3"
          >
            <ExternalReferralSearch
              getData={getDataWithoutJudgments}
              getCount={getCountWithoutJudgments}
              initialOrderColumn="exams.registrationDate"
              initialOrderDirection="DESC"
              onRowClick={handleClick}
            />
          </Tab>
          <Tab
            eventKey="activeExternalReferrals"
            title="Wszystkie"
            className="m-3"
          >
            <ExternalReferralSearch
              getData={getAllData}
              getCount={getAllCount}
              initialOrderColumn="exams.registrationDate"
              initialOrderDirection="DESC"
              onRowClick={handleClick}
            />
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
